/* Built In Imports */
import NextLink from 'next/link';
import { useMediaQuery } from 'react-responsive';
import { forwardRef, useState, useEffect } from 'react';
import React from 'react';
/* External Imports */
import {
  Box,
  Flex,
  Text,
  Image,
  Input,
  Link,
  TabList,
  TabPanel,
  TabPanels,
  useTab,
  useMultiStyleConfig,
  Button,
  Tabs,
  Popover,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { BiSearch } from 'react-icons/bi';
import { RxCross2 } from 'react-icons/rx';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@moms/Utility/StructuredTextParser';
import { refineUrlDomain } from '@moms/Utility/utils';
import config from '@config';
// import ResearchSearchFilter from '@moms/Ui/Sections/ResearchSearchFilter';
/* Styles */
import RescPrac from '@moms/styleData/RescPrac';
/* Services */

/**
 *
 * @param {Object} sectionContent
 * @param {String} region
 * @param {String} lang
 * @returns
 */

export const pageCss = {
  en: {
    tabTitle: {
      fontSize: {
        base: '16px',
        md: '22px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '1.6rem',
      },
    },
  },
};

const CustomTab = forwardRef((props, ref, lang) => {
  const tabProps = useTab({ ...props, ref });
  const isSelected = !!tabProps['aria-selected'];
  const styles = useMultiStyleConfig('Tabs', tabProps);

  return (
    <Button
      __css={styles.tab}
      {...tabProps}
      minW={'106px'}
      color={isSelected ? '#ffffff !important' : '#878787  !important'}
      fontFamily="Geist"
      mr="30px"
      fontStyle="normal"
      fontWeight="600"
      // fontSize={pageCss[lang]?.tabTitle?.fontSize}
      {...RescPrac?.[lang]?.tabtext}
      lineHeight="150.3%"
      _active={{ bg: '', borderBottom: '2px solid #EC1C2D' }}
      _focus={{ bg: '', borderBottom: '2px solid #EC1C2D' }}
      _selected={{ bg: '', borderBottom: '2px solid #EC1C2D' }}
      pos="relative"
      height={{ base: '39px', sm: '39px' }}
      p={{ base: '0', sm: '0.1rem' }}
      borderBottom={isSelected ? '2px solid #EC1C2D' : ''}
    >
      {tabProps.children}
    </Button>
  );
});

const sortCardsByYear = cards =>
  [...cards].sort((a, b) => {
    const yearA = parseInt(render(a.additionalText)?.match(/\d+/)?.[0]) || 0;
    const yearB = parseInt(render(b.additionalText)?.match(/\d+/)?.[0]) || 0;
    return yearB - yearA;
  });

const sortCardsBySessions = cards =>
  [...cards].sort((a, b) => {
    const sessionA = parseInt(a.sessions) || 0;
    const sessionB = parseInt(b.sessions) || 0;
    return sessionB - sessionA;
  });

const UpperLayout = ({
  region,
  lang,
  sectionContent,
  search,
  setSearch,
  suggestions,
  setSuggestions,
  activeSuggestion,
  setActiveSuggestion,
  handleSearch,
  handleClick,
  cards,
  setFilteredCards,
  setResults,
  filteredCards,
  data,
  isMobile,
  selectedTab,
  setSelectedTab,
  results,
}) => {
  const handleFreeText = e => {
    if (e.key === 'ArrowDown') {
      setActiveSuggestion(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      setActiveSuggestion(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
        handleClick(suggestions[activeSuggestion]);
      } else {
        const matchedCards = cards.filter(card => {
          const haystack = `${card.tags} ${card.title} ${render(
            card.description
          )} ${render(card.moreDetailsText)}`.toLowerCase();
          return haystack.includes(search.toLowerCase());
        });
        setFilteredCards(matchedCards);
        setResults(true);
      }
      setSuggestions([]);
      setActiveSuggestion(-1);
    }
  };

  const handleFocus = () => {};

  const SearchBoxSection = (
    <Box
      marginLeft="auto"
      mb={{ base: '16px', lg: '0' }}
      width={{ base: '90%', lg: 'auto' }}
      id="search"
      ml={{ base: '5%', lg: '30px' }}
    >
      <Flex
        align="center"
        display={{ base: 'block', md: 'flex' }}
        paddingLeft={{ base: '15px', md: '15px' }}
        paddingRight={{ base: '0' }}
        w="100%"
        h={{ base: 'auto', md: '44px' }}
        mx="auto"
        border="1px solid rgba(136, 136, 136, 1)"
        background="#1A1E24"
        borderRadius="8px"
        pos="relative"
        fontFamily="Geist"
        gridGap="10px"
        minH={{ base: '42px', md: '44px' }}
        maxW={{ base: '100%', lg: '328px' }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={{ lg: '100%' }}
        >
          <BiSearch fontSize="1.5rem" />
          <Input
            placeholder={data?.json?.searchPlaceholder}
            fontSize={{ base: '14px', md: '14px' }}
            w="100%"
            minW="258px"
            h={{ base: '20px', md: '44px' }}
            border="none"
            outline="none"
            focusBorderColor="white"
            pr={{base:'0', lg:'0px'}}
            mr={{ base:'0', lg:'0px' }}
            color="#ffffff"
            _placeholder={{ color: '#ffffff' }}
            onKeyDown={handleFreeText}
            onKeyUp={handleFocus}
            onChange={e => handleSearch(e.target.value)}
            value={search}
            minHeight="42px"
          />
          <Box mr={{ lg: '1.5rem' }}>
            {search && (
              <RxCross2
                fontSize="1.5rem"
                cursor="pointer"
                onClick={() => {
                  setSearch('');
                  setSuggestions([]);
                  setFilteredCards?.([]);
                  setResults?.(false);
                }}
              />
            )}
          </Box>
        </Box>

        {suggestions?.length > 0 && (
          <Box
            className="searchPopFullWidth"
            pos="absolute"
            top={{ base: '2.7rem', md: '2.7rem' }}
            maxW={'328px'}
            width="100%"
            left={{ base: '0', md: '0' }}
          >
            <Popover autoFocus={false} isOpen={search.length > 0} width="100%">
              <PopoverContent border="none" w="328px">
                <PopoverArrow />
                <PopoverBody
                  bg="#1A1E24"
                  boxShadow={`0px 1.06px 2.92px 0px #00000017, 0px 4.74px 6.75px 0px #00000024, 0px 11.79px 15.42px 0px #0000002E, 0px 22.96px 32.86px 0px #0000003B, 0px 39px 63px 0px #00000052`}
                  p={{ base: '0 0 10px 0', md: '15px 0 10px 1rem' }}
                  m="0"
                  width="100%"
                  borderRadius="6px"
                >
                  {suggestions.map((suggestion, index) => {
                    const matchLength = search.length;
                    const isMatch = suggestion.tags
                      .toLowerCase()
                      .startsWith(search.toLowerCase());

                    return (
                      <Box
                        key={index}
                        p="7px 13px"
                        lineHeight="100%"
                        fontSize="1rem"
                        _hover={{ color: '#BF3D14' }}
                        onClick={() => handleClick(suggestion)}
                        cursor="pointer"
                        bg={activeSuggestion === index ? '#2A3038' : 'none'}
                        color={activeSuggestion === index ? '#fff' : '#FFFFFF'}
                      >
                        {isMatch ? (
                          <>
                            <Box as="span">
                              {suggestion.tags.slice(0, matchLength)}
                            </Box>
                            <Box as="span">
                              {suggestion.tags.slice(matchLength)}
                            </Box>
                          </>
                        ) : (
                          suggestion.tags
                        )}
                      </Box>
                    );
                  })}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>
        )}
      </Flex>
    </Box>
  );

  const TabsSection = (
    <Box w="100%" maxW="988px" m="0 auto">
      <Tabs
        size="lg"
        variant="line"
        index={selectedTab}
        onChange={index => setSelectedTab(index)}
        align={sectionContent?.blockStyle !== 'default' ? 'end' : 'start'}
        colorScheme="primary.main"
      >
        <Box w="100%" pl={{ base:'10px', lg:'0px' }}>
          <TabList borderBottom="1px solid #414141" width={{ base:'100%', lg:'60%' }} justifyContent="flex-start">
            <CustomTab {...RescPrac?.[lang]?.tabtext}>{data?.json?.mostrecent}</CustomTab>
            <CustomTab {...RescPrac?.[lang]?.tabtext}>{data?.json?.mostviewed}</CustomTab>
          </TabList>
        </Box>
        <TabPanels w="100%" px={0} mt="-3px">
          <TabPanel pl='0' pr='0'>
            <FlexItems
              region={region}
              lang={lang}
              cards={sortCardsByYear(results ? filteredCards : cards)}
              initialDisplayCount={isMobile ? 4 : 9}
              clickResult={results ? 'yes' : undefined}
              data={data}
            />
          </TabPanel>
          <TabPanel pl='0' pr='0'>
            <FlexItems
              region={region}
              lang={lang}
              cards={sortCardsBySessions(results ? filteredCards : cards)}
              initialDisplayCount={isMobile ? 4 : 9}
              clickResult={results ? 'yes' : undefined}
              data={data}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );

  return (
    <Box
      mt="auto"
      w={{ base: '100%', lg: '100%' }}
      m="0 auto"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Box
        mb={{ base: '1.5rem', lg: '0rem' }}
        w={{ base:'100%', lg:'440px' }}
        display="flex"
        justifyContent={{base:"flex-", lg:'flex-end'}}
        position={{ base:'inherit', lg:'relative'}}
        top={{ base:'0', lg:'40px'}}
        right={{ base:'0', lg:'30px' }}
        paddingLeft={{ base:'0', lg:'10px' }}
        marginLeft={{ base:'0', lg:'auto'}}
        zIndex={'99'}
      >
        {SearchBoxSection}
      </Box>

      <Box w="100%">{TabsSection}</Box>
    </Box>
  );
};

const FlexItems = ({
  region,
  lang,
  cards,
  initialDisplayCount,
  clickResult,
  data,
}) => {
  const [loadtext, setLoadtext] = useState('Show more');
  const [rangeStart, setRangeStart] = useState(0);
  const count = initialDisplayCount;
  const visibleCards = cards.slice(0, rangeStart + count);

  const handleLoadMore = e => {
    e.preventDefault();
    setRangeStart(prev => prev + count); // adds 9 more values
  };

  return (
    <Box
      maxW={{ base: '100%', md: '990px' }}
      mx="auto"
      alignItems="center"
      justifyContent={{ base: 'center', lg: 'flex-start' }}
      mt={{ base: '0rem', lg: '2.2rem' }}
      display="flex"
      flexWrap="wrap"
      gap={{ base: '1rem', md: '1rem' }}
      id="research_details"
      minH={{ base: '300p', base: '400px' }}
      p={{ base: '16px 0px', md: '0' }}
    >
      {visibleCards.length === 0 ? (
        <Box
          w="100%"
          textAlign="center"
          fontSize="18px"
          fontWeight="400"
          color="#FFFFFF99"
          fontFamily="Geist"
          mt="3rem"
        >
          <Text {...RescPrac?.[lang]?.featureditem1} color={'#EC1C2D'}>
            {data.json.noResults}
          </Text>
        </Box>
      ) : (
        <>
          {visibleCards?.map((card, index) => (
            <ResearchCard
              key={index}
              number={(index + 1) % count}
              count={count}
              card={card}
              index={index}
              region={region}
              lang={lang}
              data={data}
            />
          ))}

          {cards.length > visibleCards.length && (
            <Box
              w="auto"
              m="10px auto 30px auto"
              alignItems={'center'}
              justifyContent="center"
            >
              <Box w="100%" textAlign="center" display={'block'}>
                <Link
                  textDecoration="none"
                  onClick={handleLoadMore}
                  variant={'outline'}
                  display="block"
                  min-width={'160px'}
                  p="10px 4px"
                  fontWeight="400"
                  borderBottom={'1px solid #EC1C2D'}
                  borderRadius={'0'}
                  color="#fff"
                  fontSize="16px"
                  fontFamily={'Geist'}
                  textAlign="center"
                  textTransform={'none'}
                  m="0 auto"
                  mb="5px"
                  _hover={{
                    textDecoration: 'none',
                    background: 'none',
                    color: '#FFFFFF',
                    borderBottom: '1px solid #636EFF',
                  }}
                  title="Show more"
                  rel="next"
                >
                  {data?.json?.showmore} ({visibleCards.length}/{cards.length})
                </Link>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const ResearchCard = ({
  key,
  number,
  count,
  card,
  index,
  region,
  lang,
  data,
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      key={index}
      bg="transparent"
      color={'#ffffff'}
      mt={{ base: '16px', md: '10px' }}
      mb={{ base: '10px', md: '0' }}
      width={{ base: '330px', md: '300px' }}
      height={{ base: '325px', md: '325px' }}
      p={{ base: '16px', md: '16px' }}
      background="rgba(255, 255, 255, 0.09)"
      box-shadow="0px 42px 89.8px 0px #99C0FF0D, 0px 167px 165.4px 0px #99C0FF0D, 0px 377px 226px 0px #99C0FF0A, 0px 669px 268px 0px #99C0FF03, 0px 1046px 249.8px 0px #99C0FF00"
    >
      <Box
        w="100%"
        color={'#ffffff'}
        display="flex"
        flexDirection="column"
        height="100%"
      >
        <Text
          textTransform={'uppercase'}
          {...RescPrac?.[lang]?.item1}
          noOfLines={'1'}
          isTruncated={'false'}
          textAlign={'left'}
        >
          {card.title}
        </Text>
        <Box
          width="100%"
          {...RescPrac?.[lang]?.item2}
          noOfLines={'4'}
          isTruncated={'false'}
        >
          <StructuredTextParser
            str={render(card?.description)}
            region={region}
            lang={lang}
            fontSize={'18px'}
            color={'#ffffff'}
          />
        </Box>
        <Box
          width="100%"
          pt="0.8rem"
          {...RescPrac?.[lang]?.item3}
          noOfLines={'5'}
          isTruncated={'false'}
        >
          <StructuredTextParser
            str={render(card?.moreDetailsText)}
            region={region}
            lang={lang}
            fontSize={'14px'}
            color={'#FFFFFF99'}
            mt={'0'}
          />
        </Box>
        <Box
          mt="auto"
          display={'flex'}
          height={'36px'}
          alignItems={'center'}
          className="bottomwidget"
        >
          {card?.buttonLink && (
            <Box>
              <NextLink
                href={refineUrlDomain(card?.buttonLink, region, lang)}
                passHref
                legacyBehavior
              >
                <Link
                  textAlign="center"
                  color="#ffffff"
                  {...RescPrac?.[lang]?.searchbuttonlink}
                  borderRadius="full"
                  border={'1px solid #ffffff'}
                  textTransform={'uppercase'}
                  target="_blank"
                  _hover={{
                    textDecoration: 'none',
                  }}
                  display={'block'}
                >
                  {data?.json?.gotoarticle}
                  <Image
                    w="13px"
                    h="13px"
                    ml="4px"
                    src={`${config.staticPath}/d/46272/1750039791-arrow-top-right.png`}
                  />
                </Link>
              </NextLink>
            </Box>
          )}
          <Box
            marginLeft={'auto'}
            {...RescPrac?.[lang]?.item5}
            color={'#FFFFFF99'}
            display={'inline-block'}
          >
            {render(card?.additionalText) !== '<p></p>' && (
              <>
                {lang !== 'hi' && data?.json?.publishedin}
                <Box display={'inline-block'} pr={lang === 'hi' ? '6px' : ''}>
                  <StructuredTextParser
                    str={render(card?.additionalText)}
                    fontSize={'14px'}
                    color={'#FFFFFF99'}
                    mt={'0'}
                  />
                </Box>
                {lang === 'hi' && data?.json?.publishedin}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MostRecentViewed = ({ region, lang, sectionContent }) => {
  const allCards = sectionContent[3]?.cards || [];
  const [selectedTab, setSelectedTab] = useState(0);
  const data = sectionContent[0]?.placeholderText;
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [tagFilters, setTagFilters] = useState(null);
  const [filterObj, setFilterObj] = useState(null);
  const [filteredCards, setFilteredCards] = useState([]);
  const [filterObjCopy, setFilterObjCopy] = useState(null);
  const [results, setResults] = useState(false);
  const [urlParams, setUrlParams] = useState(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const initialDisplayCount = isMobile ? 4 : 9;

  const handleSearch = input => {
    setSearch(input);
    if (!input || input.trim().length === 0) {
      setSuggestions([]);
      setResults?.(false);
      return;
    }

    const seen = new Set();
    const filtered = [];

    allCards.forEach(card => {
      const haystack = `${card.tags} ${card.title} ${render(
        card.description
      )} ${render(card.moreDetailsText)}`.toLowerCase();

      if (haystack.includes(input.toLowerCase())) {
        const keywords = card.tags
          ?.toLowerCase()
          .split(',')
          .map(k => k.trim())
          .filter(Boolean);

        keywords.forEach(keyword => {
          if (
            keyword.includes(input.toLowerCase()) &&
            !Array.from(seen).some(
              existing =>
                existing.includes(keyword) || keyword.includes(existing)
            )
          ) {
            seen.add(keyword);
            filtered.push({ ...card, tags: keyword });
          }
        });
      }
    });

    setSuggestions(filtered);
    setActiveSuggestion(-1);
  };

  const handleClick = suggestion => {
    const query = suggestion?.tags || '';
    const matched = allCards.filter(card => {
      const haystack = `${card.tags} ${card.title} ${render(
        card.description
      )} ${render(card.moreDetailsText)}`.toLowerCase();
      return haystack.includes(query.toLowerCase());
    });

    setSearch(query);
    setFilteredCards(matched);
    setResults(true);
    setSuggestions([]);
    setActiveSuggestion(-1);
  };

  const getSortedCards = () => {
    const source = results ? filteredCards : allCards;

    if (selectedTab === 1) {
      return sortCardsBySessions(source);
    } else {
      return sortCardsByYear(source);
    }
  };

  return (
    <Box pt="0rem" pb="6rem" color="white" m="0 auto" maxW="966px">
      <UpperLayout
        region={region}
        lang={lang}
        sectionContent={sectionContent}
        search={search}
        setSearch={setSearch}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        activeSuggestion={activeSuggestion}
        setActiveSuggestion={setActiveSuggestion}
        handleSearch={handleSearch}
        handleClick={handleClick}
        cards={allCards}
        setFilteredCards={setFilteredCards}
        setResults={setResults}
        filteredCards={filteredCards}
        data={data}
        isMobile={isMobile}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        results={results}
      />
    </Box>
  );
};

export default MostRecentViewed;
